import {
  ZonedDateTime,
  CalendarDate,
  parseZonedDateTime,
  fromDate,
  toTimeZone,
} from '@internationalized/date';

export interface ZonedDateRange {
  start: ZonedDateTime;
  end: ZonedDateTime;
}

export interface SerializedDateRange {
  start: string;
  end: string;
}

/**
 * Serialize a ZonedDateTime to ISO string with timezone for localStorage
 * Format: "2024-01-01T10:00:00[America/New_York]"
 */
export const serializeZonedDateTime = (zdt: ZonedDateTime): string => {
  return zdt.toString();
};

/**
 * Deserialize an ISO string with timezone to ZonedDateTime
 * Handles multiple formats:
 * - '2025-01-16T23:59:59.000Z' (Zulu time)
 * - '2025-01-16T23:59:59[UTC]' (bracketed timezone)
 * - '2025-01-16T23:59:59+00:00' (timezone offset)
 * - '2025-01-16T23:59:59-05:00' (timezone offset)
 * - '2025-01-16T23:59:59+00:00[UTC]' (ZonedDateTime.toString() format)
 */
export const deserializeZonedDateTime = (isoString: string): ZonedDateTime => {
  // Handle ZonedDateTime.toString() format which includes both offset and bracketed timezone
  if (
    isoString.includes('[') &&
    (isoString.includes('+') ||
      (isoString.includes('-') && isoString.lastIndexOf('-') > 10))
  ) {
    // This is already a proper ZonedDateTime string, parse it directly
    return parseZonedDateTime(isoString);
  }

  // If string has a timezone offset (+/-HH:MM) but no bracketed timezone, convert to Date first
  if (
    isoString.includes('+') ||
    (isoString.includes('-') && isoString.lastIndexOf('-') > 10)
  ) {
    // Use JavaScript Date to parse the offset, then convert to UTC ZonedDateTime
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid ISO 8601 date time string: ${isoString}`);
    }
    return fromDate(date, 'UTC');
  }

  // If string ends with 'Z', replace it with '[UTC]' for parseZonedDateTime
  const normalizedString = isoString.endsWith('Z')
    ? isoString.replace('Z', '[UTC]')
    : isoString;
  return parseZonedDateTime(normalizedString);
};

/**
 * Serialize a ZonedDateRange for localStorage
 */
export const serializeDateRange = (
  range: ZonedDateRange,
): SerializedDateRange => {
  return {
    start: serializeZonedDateTime(range.start),
    end: serializeZonedDateTime(range.end),
  };
};

/**
 * Deserialize a ZonedDateRange from localStorage
 */
export const deserializeDateRange = (
  serialized: SerializedDateRange,
): ZonedDateRange => {
  return {
    start: deserializeZonedDateTime(serialized.start),
    end: deserializeZonedDateTime(serialized.end),
  };
};

/**
 * Convert a Date to ZonedDateTime in the specified timezone
 * Useful for migrating existing Date-based state
 */
export const dateToZonedDateTime = (
  date: Date,
  timezone: string,
): ZonedDateTime => {
  // Create a ZonedDateTime from the Date's UTC values in the target timezone
  return fromDate(date, timezone);
};

/**
 * Create a ZonedDateTime for the current moment in the specified timezone
 */
export const nowInTimezone = (timezone: string): ZonedDateTime => {
  return fromDate(new Date(), timezone);
};

/**
 * Create start of day (00:00:00) in the specified timezone
 */
export const startOfDayInTimezone = (zdt: ZonedDateTime): ZonedDateTime => {
  return zdt.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
};

/**
 * Create end of day (23:59:59.999) in the specified timezone
 */
export const endOfDayInTimezone = (zdt: ZonedDateTime): ZonedDateTime => {
  return zdt.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
};

/**
 * Get the default time range for today in the specified timezone
 */
export const getDefaultTimeRange = (timezone: string): ZonedDateRange => {
  const now = nowInTimezone(timezone);
  return {
    start: startOfDayInTimezone(now),
    end: endOfDayInTimezone(now),
  };
};

/**
 * Validate that a date range is valid (start before end)
 */
export const isValidDateRange = (range: ZonedDateRange): boolean => {
  return range.start.compare(range.end) < 0;
};

/**
 * Format a ZonedDateRange for display
 */
/**
 * Format a date range for display in a specific timezone.
 *
 * If a `displayTimezone` is provided the range will be converted to that
 * timezone before formatting. When omitted we fall back to the time-zone of
 * the `start` value which preserves the previous behaviour.
 */
export const formatDateRange = (
  range: ZonedDateRange,
  displayTimezone?: string,
): string => {
  const tz = displayTimezone ?? range.start.timeZone;

  const startInTz = toTimeZone(range.start, tz);
  const endInTz = toTimeZone(range.end, tz);

  const formatter = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: tz,
  });

  const startStr = formatter.format(startInTz.toDate());
  const endStr = formatter.format(endInTz.toDate());

  return `${startStr} - ${endStr}`;
};

/**
 * Safe deserialize with fallback
 */
export const safeDeserializeDateRange = (
  serialized: SerializedDateRange | null,
  fallbackTimezone: string,
): ZonedDateRange | null => {
  if (!serialized) return null;

  try {
    return deserializeDateRange(serialized);
  } catch (error) {
    console.error('Failed to deserialize date range:', error);
    return getDefaultTimeRange(fallbackTimezone);
  }
};

/**
 * Parse manual date/time input in a specific timezone
 * Supports formats:
 * - MM/DD/YY HH:MM AM/PM - MM/DD/YY HH:MM AM/PM
 * - MM/DD/YY, HH:MM AM/PM - MM/DD/YY, HH:MM AM/PM
 * - YYYY-MM-DD HH:MM - YYYY-MM-DD HH:MM
 */
export const parseManualInputInTimezone = (
  input: string,
  timezone: string,
): ZonedDateRange => {
  const parts = input.split(' - ');
  if (parts.length !== 2) {
    throw new Error(
      'Please use format: MM/DD/YY, HH:MM AM/PM - MM/DD/YY, HH:MM AM/PM',
    );
  }

  const parseDateTime = (dateTimeStr: string): ZonedDateTime => {
    const trimmed = dateTimeStr.trim();

    // Try to parse MM/DD/YY, HH:MM AM/PM format
    const match = trimmed.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{2,4}),?\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i,
    );

    if (match) {
      const [, month, day, year, hour, minute, period] = match;
      const fullYear = year.length === 2 ? `20${year}` : year;
      const hour24 =
        period.toUpperCase() === 'PM' && hour !== '12'
          ? parseInt(hour) + 12
          : period.toUpperCase() === 'AM' && hour === '12'
            ? 0
            : parseInt(hour);

      // Create the date/time string in ISO format with the target timezone
      const isoString = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour24.toString().padStart(2, '0')}:${minute.padStart(2, '0')}:00[${timezone}]`;
      return parseZonedDateTime(isoString);
    }

    // Try to parse YYYY-MM-DD HH:MM format
    const isoMatch = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})$/,
    );
    if (isoMatch) {
      const [, year, month, day, hour, minute] = isoMatch;
      const isoString = `${year}-${month}-${day}T${hour}:${minute}:00[${timezone}]`;
      return parseZonedDateTime(isoString);
    }

    throw new Error(
      'Invalid date format. Use MM/DD/YY, HH:MM AM/PM or YYYY-MM-DD HH:MM',
    );
  };

  try {
    const start = parseDateTime(parts[0]);
    const end = parseDateTime(parts[1]);

    if (start.compare(end) >= 0) {
      throw new Error('Start date must be before end date');
    }

    return { start, end };
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes('Start date must be before end date')
    ) {
      throw error;
    }
    throw new Error(
      'Invalid date format. Use MM/DD/YY, HH:MM AM/PM - MM/DD/YY, HH:MM AM/PM',
    );
  }
};

/**
 * Helper to create ZonedDateTime from ISO string (handles Z suffix)
 * Useful for tests and API responses
 */
export const zonedDateTimeFromISO = (
  isoString: string,
  timezone?: string,
): ZonedDateTime => {
  if (timezone && timezone !== 'UTC') {
    // Parse as UTC first, then convert to desired timezone
    const utcDateTime = deserializeZonedDateTime(isoString);
    return toTimeZone(utcDateTime, timezone);
  }
  // For UTC or no timezone specified, just deserialize directly
  return deserializeZonedDateTime(isoString);
};

/**
 * Create a ZonedDateTime from a CalendarDate at the start of day (00:00:00) in the specified timezone
 */
export const createZonedDateTimeFromCalendarDate = (
  calendarDate: CalendarDate,
  timezone: string,
): ZonedDateTime => {
  // Create an ISO string at midnight in the specified timezone
  const isoString = `${calendarDate.year}-${String(calendarDate.month).padStart(2, '0')}-${String(calendarDate.day).padStart(2, '0')}T00:00:00[${timezone}]`;
  return parseZonedDateTime(isoString);
};

/**
 * Extract a CalendarDate from a ZonedDateTime
 */
export const getCalendarDateFromZonedDateTime = (
  zdt: ZonedDateTime,
): CalendarDate => {
  return new CalendarDate(zdt.year, zdt.month, zdt.day);
};

/**
 * Get start of day for a CalendarDate in the specified timezone
 */
export const getStartOfDayInTimezone = (
  calendarDate: CalendarDate,
  timezone: string,
): ZonedDateTime => {
  return createZonedDateTimeFromCalendarDate(calendarDate, timezone);
};

/**
 * Get end of day for a CalendarDate in the specified timezone
 */
export const getEndOfDayInTimezone = (
  calendarDate: CalendarDate,
  timezone: string,
): ZonedDateTime => {
  const startOfDay = createZonedDateTimeFromCalendarDate(
    calendarDate,
    timezone,
  );
  return startOfDay.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
};
